import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";

/**
 * CommonCalendar — day/week/month view shared across roles.
 *
 * Props:
 *   events           — [{id, start (ISO), duration_minutes, title, subtitle, color, status,
 *                        doctor_id, doctor_name}]
 *   view             — "day" | "week" | "month"  (default "week")
 *   anchorDate       — Date (defaults today). Component manages its own anchor when `controlled` is false.
 *   onSelectEvent    — (event) => void
 *   onSelectSlot     — (datetime ISO, durationMinutes) => void   (day & week only)
 *   onRangeChange    — ({from, to}) => void  Fires when user navigates; parent should refetch
 *   onViewChange     — (newView) => void
 *   dayStartHour     — default 8
 *   dayEndHour       — default 20
 *   slotMinutes      — default 30
 */
const STATUS_BORDER = {
  Scheduled: "border-l-mg-medical",
  Confirmed: "border-l-emerald-500",
  "Checked In": "border-l-amber-500",
  "In Consultation": "border-l-mg-genie",
  Completed: "border-l-slate-400",
  Cancelled: "border-l-red-400 opacity-70 line-through",
  "No Show": "border-l-red-600 opacity-70",
  Expired: "border-l-amber-500 opacity-70",
  Rescheduled: "border-l-purple-500",
};

const startOfWeek = (d) => {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const dow = x.getDay(); // 0=Sun
  x.setDate(x.getDate() - dow);
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const fmtHour = (h) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr} ${ampm}`;
};
const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export function CommonCalendar({
  events = [], view = "week", anchorDate, onSelectEvent, onSelectSlot, onRangeChange, onViewChange,
  dayStartHour = 8, dayEndHour = 20, slotMinutes = 30, loading = false,
}) {
  const [internalAnchor, setInternalAnchor] = useState(anchorDate || new Date());
  const anchor = anchorDate || internalAnchor;

  const setAnchor = (next) => {
    setInternalAnchor(next);
    if (onRangeChange) {
      const range = computeRange(next, view);
      onRangeChange(range);
    }
  };
  const setView = (next) => { if (onViewChange) onViewChange(next); };

  const navigate = (delta) => {
    let next;
    if (view === "day") next = addDays(anchor, delta);
    else if (view === "week") next = addDays(anchor, delta * 7);
    else next = new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1);
    setAnchor(next);
  };

  // Group events by ISO date
  const byDay = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      const d = new Date(e.start);
      const key = d.toISOString().slice(0, 10);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push({ ...e, _date: d });
    }
    for (const arr of m.values()) arr.sort((a, b) => a._date - b._date);
    return m;
  }, [events]);

  const title = view === "day"
    ? anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : view === "week"
      ? `Week of ${startOfWeek(anchor).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="mg-card p-0 overflow-hidden" data-testid="common-calendar">
      <div className="flex items-center justify-between p-3 border-b border-slate-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="cal-prev" aria-label="Previous period"><ChevronLeft className="w-4 h-4" aria-hidden="true" /></button>
          <button onClick={() => setAnchor(new Date())} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50" data-testid="cal-today">Today</button>
          <button onClick={() => navigate(1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="cal-next" aria-label="Next period"><ChevronRight className="w-4 h-4" aria-hidden="true" /></button>
          <div className="font-bold text-mg-navy ml-2" data-testid="cal-title"><CalIcon className="w-4 h-4 inline mr-1 text-mg-medical" />{title}</div>
        </div>
        <div className="flex border border-slate-200 rounded overflow-hidden text-xs">
          {["day", "week", "month"].map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 ${view === v ? "bg-mg-medical text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`cal-view-${v}`}>{v}</button>
          ))}
        </div>
      </div>

      {loading && <div className="text-center text-slate-400 py-12 text-sm">Loading…</div>}
      {!loading && view === "day" && <DayView anchor={anchor} byDay={byDay} dayStartHour={dayStartHour} dayEndHour={dayEndHour} slotMinutes={slotMinutes} onSelectEvent={onSelectEvent} onSelectSlot={onSelectSlot} />}
      {!loading && view === "week" && <WeekView anchor={anchor} byDay={byDay} dayStartHour={dayStartHour} dayEndHour={dayEndHour} slotMinutes={slotMinutes} onSelectEvent={onSelectEvent} onSelectSlot={onSelectSlot} />}
      {!loading && view === "month" && <MonthView anchor={anchor} byDay={byDay} onSelectEvent={onSelectEvent} />}
    </div>
  );
}

function computeRange(anchor, view) {
  if (view === "day") {
    const s = new Date(anchor); s.setHours(0, 0, 0, 0);
    const e = new Date(anchor); e.setHours(23, 59, 59, 999);
    return { from: s.toISOString().slice(0, 10), to: e.toISOString().slice(0, 10) };
  }
  if (view === "week") {
    const s = startOfWeek(anchor);
    const e = addDays(s, 6); e.setHours(23, 59, 59, 999);
    return { from: s.toISOString().slice(0, 10), to: e.toISOString().slice(0, 10) };
  }
  const s = startOfMonth(anchor);
  const e = endOfMonth(anchor); e.setHours(23, 59, 59, 999);
  return { from: s.toISOString().slice(0, 10), to: e.toISOString().slice(0, 10) };
}

// =============================================================
// DAY VIEW
// =============================================================
function DayView({ anchor, byDay, dayStartHour, dayEndHour, slotMinutes, onSelectEvent, onSelectSlot }) {
  const key = anchor.toISOString().slice(0, 10);
  const evs = byDay.get(key) || [];
  const slots = [];
  for (let h = dayStartHour; h < dayEndHour; h++) {
    for (let m = 0; m < 60; m += slotMinutes) slots.push({ h, m });
  }
  return (
    <div className="grid grid-cols-[60px_1fr] divide-x divide-slate-100 max-h-[700px] overflow-y-auto" data-testid="cal-day-view">
      <div>
        {slots.map(({ h, m }, i) => (
          <div key={i} className="h-12 text-[10px] text-slate-400 px-1 text-right pt-0.5">
            {m === 0 ? fmtHour(h) : ""}
          </div>
        ))}
      </div>
      <div className="relative">
        {slots.map(({ h, m }, i) => {
          const dt = new Date(anchor); dt.setHours(h, m, 0, 0);
          return (
            <button key={i} onClick={() => onSelectSlot && onSelectSlot(dt.toISOString(), slotMinutes)}
              className={`block w-full h-12 border-b border-slate-50 hover:bg-mg-medical/5 text-left ${m === 0 ? "border-slate-100" : ""}`}
              data-testid={`cal-day-slot-${h}-${m}`} />
          );
        })}
        {evs.map((e) => {
          const startMin = e._date.getHours() * 60 + e._date.getMinutes();
          const offsetMin = startMin - dayStartHour * 60;
          if (offsetMin < 0) return null;
          const top = (offsetMin / slotMinutes) * 48;
          const height = Math.max(((e.duration_minutes || 30) / slotMinutes) * 48 - 2, 24);
          return (
            <EventChip key={e.id} ev={e} style={{ top: `${top}px`, height: `${height}px`, left: "4px", right: "4px" }}
              onClick={() => onSelectEvent && onSelectEvent(e)} />
          );
        })}
      </div>
    </div>
  );
}

// =============================================================
// WEEK VIEW
// =============================================================
function WeekView({ anchor, byDay, dayStartHour, dayEndHour, slotMinutes, onSelectEvent, onSelectSlot }) {
  const start = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const today = new Date();
  const slotsPerHour = 60 / slotMinutes;
  const slotCount = (dayEndHour - dayStartHour) * slotsPerHour;
  const rowHeight = 40;
  const colMinHeight = slotCount * rowHeight;
  return (
    <div className="overflow-x-auto" data-testid="cal-week-view">
      {/* Header row */}
      <div className="grid grid-cols-[60px_repeat(7,minmax(110px,1fr))] border-b border-slate-100 min-w-[800px]">
        <div className="bg-slate-50" />
        {days.map((d, i) => (
          <div key={i} className={`text-center py-2 text-xs ${sameDay(d, today) ? "bg-mg-medical/10 text-mg-medical font-bold" : "text-slate-600"}`}>
            <div className="uppercase tracking-wider">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
            <div className="text-lg font-bold">{d.getDate()}</div>
          </div>
        ))}
      </div>
      {/* Body row: hour gutter + 7 day columns */}
      <div className="grid grid-cols-[60px_repeat(7,minmax(110px,1fr))] min-w-[800px] max-h-[640px] overflow-y-auto">
        {/* Hour gutter */}
        <div>
          {Array.from({ length: slotCount }).map((_, idx) => {
            const h = dayStartHour + Math.floor(idx / slotsPerHour);
            const m = (idx % slotsPerHour) * slotMinutes;
            return (
              <div key={idx} className={`h-10 text-[10px] text-slate-400 text-right pr-1 pt-0.5 ${m === 0 ? "border-t border-slate-100" : ""}`}>
                {m === 0 ? fmtHour(h) : ""}
              </div>
            );
          })}
        </div>
        {/* Day columns */}
        {days.map((d, di) => {
          const key = d.toISOString().slice(0, 10);
          const evs = byDay.get(key) || [];
          return (
            <div key={di} className="relative border-l border-slate-100" style={{ minHeight: `${colMinHeight}px` }}>
              {/* Background slots */}
              {Array.from({ length: slotCount }).map((_, idx) => {
                const h = dayStartHour + Math.floor(idx / slotsPerHour);
                const m = (idx % slotsPerHour) * slotMinutes;
                const dt = new Date(d); dt.setHours(h, m, 0, 0);
                return (
                  <button key={idx} onClick={() => onSelectSlot && onSelectSlot(dt.toISOString(), slotMinutes)}
                    className={`block w-full h-10 hover:bg-mg-medical/5 ${m === 0 ? "border-t border-slate-100" : ""}`}
                    data-testid={`cal-week-slot-${di}-${h}-${m}`} />
                );
              })}
              {/* Events */}
              {evs.map((e) => {
                const startMin = e._date.getHours() * 60 + e._date.getMinutes();
                const offsetMin = startMin - dayStartHour * 60;
                if (offsetMin < 0 || offsetMin >= slotCount * slotMinutes) return null;
                const top = (offsetMin / slotMinutes) * rowHeight;
                const height = Math.max(((e.duration_minutes || 30) / slotMinutes) * rowHeight - 2, 22);
                return (
                  <EventChip key={e.id} ev={e}
                    style={{ top: `${top}px`, height: `${height}px`, left: "2px", right: "2px" }}
                    compact onClick={() => onSelectEvent && onSelectEvent(e)} />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================
// MONTH VIEW
// =============================================================
function MonthView({ anchor, byDay, onSelectEvent }) {
  const first = startOfMonth(anchor);
  const last = endOfMonth(anchor);
  const startGrid = addDays(first, -first.getDay());
  const totalCells = Math.ceil((last.getDate() + first.getDay()) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => addDays(startGrid, i));
  const today = new Date();
  return (
    <div data-testid="cal-month-view">
      <div className="grid grid-cols-7 text-center text-xs uppercase tracking-wider text-slate-500 py-2 border-b border-slate-100">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 divide-x divide-y divide-slate-100">
        {cells.map((d, i) => {
          const key = d.toISOString().slice(0, 10);
          const evs = byDay.get(key) || [];
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          return (
            <div key={i} className={`min-h-[100px] p-1.5 ${inMonth ? "bg-white" : "bg-slate-50/40 text-slate-400"} ${isToday ? "ring-2 ring-mg-medical ring-inset" : ""}`} data-testid={`cal-month-cell-${key}`}>
              <div className={`text-xs font-semibold ${isToday ? "text-mg-medical" : "text-slate-600"}`}>{d.getDate()}</div>
              <div className="space-y-0.5 mt-1">
                {evs.slice(0, 3).map((e) => (
                  <button key={e.id} onClick={() => onSelectEvent && onSelectEvent(e)}
                    className={`block w-full text-left px-1.5 py-0.5 rounded text-[10px] truncate hover:opacity-80 border-l-2 ${STATUS_BORDER[e.status] || "border-l-mg-medical"}`}
                    style={{ background: (e.color || "#2090CF") + "20" }}
                    data-testid={`cal-month-event-${e.id}`}>
                    <span className="font-semibold">{fmtTime(e._date)}</span> {e.title}
                  </button>
                ))}
                {evs.length > 3 && <div className="text-[10px] text-slate-500 pl-1">+{evs.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================
// EVENT CHIP
// =============================================================
function EventChip({ ev, style, onClick, compact }) {
  return (
    <button onClick={onClick} style={{ ...(style || {}), background: (ev.color || "#2090CF") + "25" }}
      className={`absolute rounded text-left px-1.5 py-0.5 border-l-2 hover:opacity-90 ${STATUS_BORDER[ev.status] || "border-l-mg-medical"} overflow-hidden`}
      data-testid={`cal-event-${ev.id}`}>
      <div className="text-[10px] font-bold text-mg-navy truncate">{fmtTime(ev._date)} {ev.title}</div>
      {!compact && <div className="text-[10px] text-slate-500 truncate">{ev.subtitle || ""}{ev.doctor_name ? ` · ${ev.doctor_name}` : ""}</div>}
    </button>
  );
}
